import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Loader2,
  AlertTriangle,
  KeyRound,
  Copy,
  Check,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import { registryCredentials } from '../api';
import type { IssuedRegistryCredential } from '../api';
import { useDateFormat } from '../lib/dates';

/**
 * Your own credential for pulling images from our registry.
 *
 * Self-service because the alternative is what we have today: everyone uses one
 * shared account, so nobody's access can be taken away when they leave, and the
 * registry cannot say who pulled what. It is also the preparation for token auth
 * — by the time basic auth is switched off, everyone already has their own.
 *
 * The password is shown exactly once. The PC stores it encrypted, but only to be
 * able to rewrite the proxy's access list; it is never handed out again.
 */

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 bg-surface px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
      {copied ? 'Kopiert' : label}
    </button>
  );
}

function IssuedCredential({ issued }: { issued: IssuedRegistryCredential }) {
  return (
    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <p className="text-sm font-medium text-amber-900">
          {issued.rotated ? 'Neues Passwort gesetzt.' : 'Zugang angelegt.'} Das Passwort steht
          hier zum einzigen Mal — es lässt sich später nicht wieder anzeigen, nur ersetzen.
        </p>
      </div>

      <div className="space-y-2">
        <div>
          <div className="text-xs font-medium text-slate-600">Benutzername</div>
          <div className="mt-0.5 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-surface px-2 py-1.5 font-mono text-xs text-slate-800">
              {issued.username}
            </code>
            <CopyButton value={issued.username} label="Kopieren" />
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-slate-600">Passwort</div>
          <div className="mt-0.5 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-surface px-2 py-1.5 font-mono text-xs text-slate-800">
              {issued.password}
            </code>
            <CopyButton value={issued.password} label="Kopieren" />
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-medium text-slate-600">Anmelden</div>
        <pre className="mt-0.5 overflow-x-auto rounded bg-konsole px-3 py-2 font-mono text-[11px] leading-relaxed text-konsole-hell">
          {`echo '${issued.password}' | ${issued.loginCommand}`}
        </pre>
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="text-[11px] leading-snug text-slate-500">
            Über die Standardeingabe, damit das Passwort nicht in der Prozessliste und in der
            Shell-Historie landet. Wer die Historie führt: mit einem Leerzeichen davor
            aufrufen.
          </p>
          <CopyButton
            value={`echo '${issued.password}' | ${issued.loginCommand}`}
            label="Befehl"
          />
        </div>
      </div>
    </div>
  );
}

export default function RegistryCredentialModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { dateTime } = useDateFormat();
  const [issued, setIssued] = React.useState<IssuedRegistryCredential | null>(null);
  const [error, setError] = React.useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['registry-credential-me'],
    queryFn: registryCredentials.mine,
  });

  const fail = (err: unknown, fallback: string) =>
    setError(
      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : fallback),
    );

  const issue = useMutation({
    mutationFn: registryCredentials.issueMine,
    onSuccess: (result) => {
      setIssued(result);
      setError('');
      void qc.invalidateQueries({ queryKey: ['registry-credential-me'] });
      void qc.invalidateQueries({ queryKey: ['registry-credentials'] });
    },
    onError: (err) => fail(err, 'Zugang konnte nicht angelegt werden.'),
  });

  const revoke = useMutation({
    mutationFn: registryCredentials.revokeMine,
    onSuccess: () => {
      setIssued(null);
      setError('');
      void qc.invalidateQueries({ queryKey: ['registry-credential-me'] });
      void qc.invalidateQueries({ queryKey: ['registry-credentials'] });
    },
    onError: (err) => fail(err, 'Zugang konnte nicht entzogen werden.'),
  });

  const existing = data?.credential ?? null;
  const busy = issue.isPending || revoke.isPending;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4">
      <div className="mt-12 w-full max-w-xl rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-slate-400" />
            <h2 className="text-base font-semibold text-slate-900">{t('registry.myAccount')}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {isLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 size={15} className="animate-spin" /> Lade…
            </div>
          )}

          {data && !data.available && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-snug text-amber-800">
              Eigene Registry-Zugänge sind noch nicht eingerichtet — im PC fehlt{' '}
              <span className="font-mono">{data.missing.join(', ')}</span>. Bis dahin läuft
              alles über den gemeinsamen Zugang.
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {issued && <IssuedCredential issued={issued} />}

          {data?.available && !issued && (
            <>
              {existing ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="font-mono text-sm text-slate-800">{existing.username}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    Angelegt {dateTime(existing.createdAt)}
                    {existing.rotatedAt && ` · zuletzt erneuert ${dateTime(existing.rotatedAt)}`}
                  </div>
                  <p className="mt-2 text-xs leading-snug text-slate-500">
                    Das Passwort ist nur beim Anlegen zu sehen. Vergessen heißt: neues setzen —
                    der Benutzername bleibt, die alte Anmeldung gilt danach nicht mehr.
                  </p>
                </div>
              ) : (
                <p className="text-sm leading-snug text-slate-600">
                  Du hast noch keinen eigenen Zugang zu{' '}
                  <span className="font-mono">{data.registry}</span>. Mit einem eigenen lässt
                  sich später genau dein Zugang entziehen, ohne alle anderen mitzunehmen — und
                  die Registry weiß, wer gezogen hat.
                </p>
              )}
            </>
          )}
        </div>

        {data?.available && (
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-6 py-4">
            {existing ? (
              <button
                onClick={() => {
                  if (
                    confirm(
                      `Zugang "${existing.username}" entziehen?\n\nDanach kannst du keine Images mehr ziehen, bis du einen neuen anlegst.`,
                    )
                  )
                    revoke.mutate();
                }}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {revoke.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Entziehen
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={() => {
                if (
                  !existing ||
                  confirm(
                    'Neues Passwort setzen?\n\nDie bisherige Anmeldung gilt danach nicht mehr — überall neu anmelden, wo dieser Zugang benutzt wird.',
                  )
                )
                  issue.mutate();
              }}
              disabled={busy}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium text-white disabled:opacity-50',
                'bg-brand-600 hover:bg-brand-700',
              )}
            >
              {issue.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : existing ? (
                <RefreshCw size={14} />
              ) : (
                <KeyRound size={14} />
              )}
              {existing ? 'Neues Passwort' : 'Zugang anlegen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
